// MoreSheet.jsx
// The hamburger replacement: a drag-to-dismiss bottom sheet holding the entire
// secondary IA + account. Dashboard stays visible behind the dim so dismissing
// feels like returning, not leaving. (Design: "Mobile Native Feel" → More.)
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, Icon, haptics, hapticsEnabled, setHapticsEnabled } from "../ds";
import { useAuth } from "../../contexts/AuthContext";
import { useStateContext } from "../../contexts/ContextProvider";
import { themeColors } from "../../data/staticdata";
import { filterAdminOnly } from "./adminOnly";

// Row `icon` = DS Icon name (components/ds/icons.jsx). Rendered monochrome
// (fg-2 on a bg-3 tile) per the 90/8/2 rule — accent stays on the Done pill
// and active states only.
const GROUPS = [
  {
    group: "Trading",
    rows: [
      { label: "Manual Trading", sub: "Place orders directly", route: "/TradeSetup", icon: "trade" },
      { label: "Algo Trading", sub: "Live strategy monitor", route: "/AlgoDashboard", icon: "algo" },
      { label: "Order History", sub: "Orders & fills", route: "/Orders", icon: "orders" },
      { label: "Backtesting", sub: "Strategy runs", route: "/Strategies/Modern/Backtesting", icon: "flask" },
    ],
  },
  {
    // Group name mirrors the desktop sidebar's "Broker Platforms" SECTION; the row
    // is the page inside it. The row used to borrow the section's name, which left
    // the screen itself unnamed here.
    group: "Broker Platforms",
    rows: [
      { label: "Broker Accounts", sub: "Flattrade · ICICI Breeze", route: "/TradeAccounts", icon: "bank" },
    ],
  },
  {
    //! Admin is a SECTION, not a page. This used to be one "Admin" row pointing
    //! at /Admin — which lands on Live Connections only — so Batch Jobs, Broker
    //! Management, User Management and Market Data were unreachable on mobile
    //! (registered routes with no way in short of typing the URL), even though
    //! the row's own subtitle advertised "batch jobs".
    //! Order and membership mirror the desktop sidebar's Admin group
    //! (data/staticdata.js) EXACTLY, so the two navs teach the same IA — a
    //! screen must not live under Admin on desktop and somewhere else here.
    group: "Admin",
    rows: [
      { label: "Live Connections", sub: "Active sessions & devices", route: "/Admin/LiveConnections", icon: "eye" },
      { label: "Batch Jobs", sub: "Scheduled runs & schedules", route: "/Admin/BatchJobs", icon: "calendar" },
      { label: "Models", sub: "Model registry", route: "/Admin/Models", icon: "algo", adminOnly: true },
      { label: "Trade Economics", sub: "Charges & commissions", route: "/Admin/TradeEconomics", icon: "stats", adminOnly: true },
      { label: "Instrument Master", sub: "NSE · BSE · F&O", route: "/InstrumentMaster", icon: "data" },
      { label: "Broker Management", sub: "Platforms & credentials", route: "/BrokerManagement", icon: "bank" },
      { label: "User Management", sub: "Roles & access", route: "/user-management", icon: "user" },
      { label: "Market Data", sub: "Feeds & subscriptions", route: "/MarketDataManagement", icon: "stats" },
    ],
  },
  {
    group: "Account",
    rows: [{ label: "Profile", sub: "Your account", route: "/profile", icon: "user" }],
  },
];

function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--fg-3)" strokeWidth="2">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export default function MoreSheet({ open, onClose }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentMode, setMode, currentColor, setColor } = useStateContext();
  const isDark = currentMode === "Dark";
  const [hapticsOn, setHapticsOn] = useState(() => hapticsEnabled());

  const go = (route) => {
    onClose?.();
    navigate(route);
  };

  const toggleTheme = () =>
    setMode({ target: { value: isDark ? "Light" : "Dark" } });

  const toggleHaptics = () => {
    const next = !hapticsOn;
    setHapticsEnabled(next);
    setHapticsOn(next);
    if (next) haptics.tap(); // sample so the toggle-on state is felt immediately
  };

  return (
    <Sheet open={open} onClose={onClose} title="More">
            {GROUPS.map((g, gi) => {
              const rows = filterAdminOnly(g.rows, user);
              return (
              <div key={g.group} style={{ marginBottom: gi === GROUPS.length - 1 ? 12 : 18 }}>
                <div
                  style={{
                    fontSize: 10,
                    color: "var(--fg-3)",
                    letterSpacing: ".10em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: 6,
                    paddingLeft: 4,
                  }}
                >
                  {g.group}
                </div>
                <div
                  style={{
                    background: "var(--bg-3)",
                    borderRadius: 12,
                    border: "1px solid var(--border)",
                    overflow: "hidden",
                  }}
                >
                  {rows.map((r, ri) => (
                    <button
                      key={r.label}
                      type="button"
                      onClick={() => go(r.route)}
                      style={{
                        appearance: "none",
                        width: "100%",
                        cursor: "pointer",
                        textAlign: "left",
                        background: "transparent",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "13px 14px",
                        borderBottom:
                          ri === rows.length - 1 ? "none" : "1px solid var(--border)",
                        color: "var(--fg-1)",
                      }}
                    >
                      <span
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: "var(--bg-3)",
                          border: "1px solid var(--border)",
                          flex: "0 0 auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--fg-2)",
                        }}
                      >
                        <Icon name={r.icon} size={16} />
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 14, fontWeight: 500 }}>
                          {r.label}
                        </span>
                        <span
                          style={{
                            display: "block",
                            fontSize: 11,
                            color: "var(--fg-3)",
                            marginTop: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {r.sub}
                        </span>
                      </span>
                      <Chevron />
                    </button>
                  ))}
                </div>
              </div>
              );
            })}

            {/* Appearance toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              style={{
                appearance: "none",
                width: "100%",
                cursor: "pointer",
                textAlign: "left",
                padding: "13px 14px",
                background: "var(--bg-3)",
                borderRadius: 12,
                border: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "var(--fg-1)",
                marginBottom: 12,
              }}
            >
              <span>
                <span style={{ display: "block", fontSize: 14, fontWeight: 500 }}>Appearance</span>
                <span style={{ display: "block", fontSize: 11, color: "var(--fg-3)", marginTop: 1 }}>
                  {isDark ? "Dark" : "Light"} theme · tap to switch
                </span>
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--color-accent)",
                  padding: "4px 10px",
                  borderRadius: 999,
                  background: "rgb(var(--color-accent-rgb) / 0.12)",
                }}
              >
                {isDark ? "Light" : "Dark"}
              </span>
            </button>

            {/* Accent color — same palette as the web Appearance settings. */}
            <div
              style={{
                padding: "13px 14px",
                background: "var(--bg-3)",
                borderRadius: 12,
                border: "1px solid var(--border)",
                marginBottom: 12,
              }}
            >
              <span style={{ display: "block", fontSize: 14, fontWeight: 500, color: "var(--fg-1)" }}>
                Accent color
              </span>
              <span style={{ display: "block", fontSize: 11, color: "var(--fg-3)", marginTop: 1, marginBottom: 12 }}>
                Used across highlights &amp; controls
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {themeColors.map((item) => {
                  const active = item.color === currentColor;
                  return (
                    <button
                      key={item.name}
                      type="button"
                      aria-label={item.name}
                      aria-pressed={active}
                      onClick={() => {
                        setColor(item.color);
                        haptics.tap();
                      }}
                      style={{
                        appearance: "none",
                        cursor: "pointer",
                        height: 34,
                        width: 34,
                        borderRadius: 999,
                        border: "none",
                        padding: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: item.color,
                        boxShadow: active ? `0 0 0 2px var(--bg-3), 0 0 0 4px ${item.color}` : "none",
                      }}
                    >
                      {active && <Icon name="success" size={16} className="text-white" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Haptics toggle */}
            <button
              type="button"
              onClick={toggleHaptics}
              style={{
                appearance: "none",
                width: "100%",
                cursor: "pointer",
                textAlign: "left",
                padding: "13px 14px",
                background: "var(--bg-3)",
                borderRadius: 12,
                border: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "var(--fg-1)",
                marginBottom: 12,
              }}
            >
              <span>
                <span style={{ display: "block", fontSize: 14, fontWeight: 500 }}>Haptics</span>
                <span style={{ display: "block", fontSize: 11, color: "var(--fg-3)", marginTop: 1 }}>
                  Vibration feedback
                </span>
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--color-accent)",
                  padding: "4px 10px",
                  borderRadius: 999,
                  background: "rgb(var(--color-accent-rgb) / 0.12)",
                }}
              >
                {hapticsOn ? "On" : "Off"}
              </span>
            </button>

            {/* Sign out — neutral, red text only, below the fold */}
            <button
              type="button"
              onClick={() => {
                onClose?.();
                logout();
              }}
              style={{
                appearance: "none",
                width: "100%",
                cursor: "pointer",
                textAlign: "left",
                padding: "13px 14px",
                background: "var(--bg-3)",
                borderRadius: 12,
                border: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>
                <span style={{ display: "block", fontSize: 14, fontWeight: 500, color: "var(--danger)" }}>
                  Sign out
                </span>
                <span style={{ display: "block", fontSize: 11, color: "var(--fg-3)", marginTop: 1 }}>
                  Ends your DeeTrade session
                </span>
              </span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
            </button>
    </Sheet>
  );
}
